import { State } from './StateMachine';
import { WebGLRenderer } from '../renderer/WebGLRenderer';
import { TextRenderer } from '../ui/TextRenderer';
import { TextInputHandler } from '../core/TextInputHandler';
import { HighScoreManager } from '../core/HighScoreManager';
import { CANVAS_WIDTH } from '../utils/constants';

// Helper to center text (8px per character)
function centerX(text: string): number {
  return (CANVAS_WIDTH - text.length * 8) / 2;
}

export class HighScoreEntryState implements State {
  private renderer: WebGLRenderer;
  private textRenderer: TextRenderer;
  private textInput: TextInputHandler;
  private highScoreManager: HighScoreManager;
  private score: number = 0;
  private level: number = 1;
  private submitting: boolean = false;
  private onComplete: () => void;
  private onCancel: () => void;

  constructor(
    renderer: WebGLRenderer,
    textRenderer: TextRenderer,
    textInput: TextInputHandler,
    highScoreManager: HighScoreManager,
    onComplete: () => void,
    onCancel: () => void
  ) {
    this.renderer = renderer;
    this.textRenderer = textRenderer;
    this.textInput = textInput;
    this.highScoreManager = highScoreManager;
    this.onComplete = onComplete;
    this.onCancel = onCancel;
  }

  setScoreData(score: number, level: number): void {
    this.score = score;
    this.level = level;
  }

  enter(): void {
    this.submitting = false;
    this.textInput.activate();
  }

  exit(): void {
    this.textInput.deactivate();
  }

  handleInput(): void {
    if (this.submitting) return;

    if (this.textInput.consumeCancel()) {
      this.onCancel();
      return;
    }

    if (this.textInput.consumeSubmit() && this.textInput.isValid()) {
      this.submitScore();
    }
  }

  update(dt: number): void {
    this.textInput.update(dt);
  }

  private async submitScore(): Promise<void> {
    if (this.submitting) return;

    this.submitting = true;
    const playerName = this.textInput.getText();

    const success = await this.highScoreManager.submitScore(playerName, this.score, this.level);

    if (success) {
      // Reload scores after submission
      await this.highScoreManager.loadScores();
    }

    this.onComplete();
  }

  render(): void {
    this.renderer.clear();
    this.renderer.beginText();

    // Title
    const title = 'NEW HIGH SCORE';
    this.textRenderer.draw(title, centerX(title), 60, 1, 1, 0); // Yellow

    // Score display
    const scoreText = `SCORE ${this.score}`;
    this.textRenderer.draw(scoreText, centerX(scoreText), 100);

    // Level display
    const levelText = `LEVEL ${this.level}`;
    this.textRenderer.draw(levelText, centerX(levelText), 120);

    if (this.submitting) {
      // Show submitting message
      const submittingText = 'SUBMITTING';
      this.textRenderer.draw(submittingText, centerX(submittingText), 160);
    } else {
      // Name entry prompt
      const enterName = 'ENTER YOUR NAME';
      this.textRenderer.draw(enterName, centerX(enterName), 160);

      // Name input field with cursor
      const nameText = this.textInput.getDisplayText();
      const nameDisplay = `> ${nameText.padEnd(10, ' ')} <`;
      this.textRenderer.draw(nameDisplay, centerX(nameDisplay), 190, 0, 1, 1); // Cyan

      // Error message if any
      const error = this.textInput.getError();
      if (error) {
        this.textRenderer.draw(error, centerX(error), 220, 1, 0, 0); // Red
      }

      // Instructions
      const instructions = 'PRESS ENTER TO SUBMIT';
      this.textRenderer.draw(instructions, centerX(instructions), 250);

      const escText = 'ESC TO CANCEL';
      this.textRenderer.draw(escText, centerX(escText), 270);
    }

    this.renderer.endBatch();
  }
}
